// #region Imports
import { DateCell } from "_shared/_views/tables/date-cells";
import useSessionPermissionsLoadState from "permissions/session/use-session-permissions-load-state";
import { styled } from "@mui/material";
import Link from "@mui/material/Link";
import Tooltip from "@mui/material/Tooltip";
import { skipToken } from "@reduxjs/toolkit/query";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import CondensedCell from "_shared/_views/tables/condensed-cell";
import RowActions from "_shared/_views/tables/row-actions";
import RowLink from "_shared/_views/tables/row-link";
import SearchMatch, { SearchMatchScope } from "_shared/_views/tables/search-match";
import { MRTNoMemo } from "_shared/_views/tables/table";
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import { searchTextFieldProps, topToolbarFitsThePage } from "_shared/_views/tables/table-toolbar";
import { LIST_PAGE_SIZE } from "_shared/_views/tables/table-page-size";
import type { ListChipCounts, ListChipSelection } from "_shared/_views/list-pages/list-chip-selection";
import type { ListPageTab } from "_shared/_views/list-pages/list-page-heading";
import { useCurrentTableView } from "_shared/_views/list-pages/use-current-table-view";
import AHBoolean from "_shared/booleans/boolean";
import Guid from "_shared/guids/guid";
import {
  type MRT_ColumnFiltersState as ColumnFiltersState,
  createMRTColumnHelper,
  type MRT_Row,
  type MRT_TableInstance,
  type MRT_TableOptions,
  type MRT_PaginationState as PaginationState,
  type MRT_SortingState as SortingState,
  type MRT_Updater,
} from "material-react-table";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { getSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import React, { useCallback, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { Link as RouterLink, useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import Session from "users/__session/session";
import { useSession } from "users/__session/session-context";
import {
  useArchiveFeeScheduleTemplateMutation,
  useDeleteFeeScheduleTemplateMutation,
  useFeeScheduleTemplateListCounts,
  useGetFeeScheduleTemplatesByIdsQuery,
  useGetFeeScheduleTemplatesQuery,
  useUnarchiveFeeScheduleTemplateMutation
} from "work/fee-schedule-templates/_api/fee-schedule-template-api";
import FeeScheduleTemplateQuery from "work/fee-schedule-templates/_api/queries/contracts/fee-schedule-template-query";
import { FeeScheduleTemplateFilterParameters } from "work/fee-schedule-templates/_api/queries/contracts/filtering/fee-schedule-template-filter-parameters";
import {
  FeeScheduleTemplateOrderParameters
} from "work/fee-schedule-templates/_api/queries/contracts/ordering/fee-schedule-template-order-parameters";
import {
  FeeScheduleTemplateLoadIssue,
  getFeeScheduleTemplateQueryByInterface,
  getFeeScheduleTemplatesByIds,
  getFeeScheduleTemplatesByQuery,
  getLastSuccessfulFeeScheduleTemplateQueryByInterface,
  isFeeScheduleTemplateLoadIssue,
  resetFeeScheduleTemplatesByQueryState,
  updateFeeScheduleTemplateInterfaceQuery,
} from "work/fee-schedule-templates/_store/fee-schedule-templates-redux-slice";
import FeeScheduleRowActions from "work/fee-schedule-templates/_views/_row-actions/fee-schedule-row-actions";
import FeeScheduleInternalActionsToolbar from "work/fee-schedule-templates/_views/_toolbars/fee-schedule-internal-actions-toolbar";
import FeeScheduleToolbar from "work/fee-schedule-templates/_views/_toolbars/fee-schedule-toolbar";
import {
  feeScheduleTabPath,
  FEE_SCHEDULES_CURRENT_VIEW,
  listsArchive,
  readFeeScheduleChips,
  readFeeScheduleTab,
  STAGE_PARAMETER,
  writeFeeScheduleChips,
} from "work/fee-schedule-templates/_views/pages/fee-schedule-list-chips";
import { PageTab } from "work/fee-schedule-templates/_views/pages/tabs";
import { TemplateTagLine } from "work/fee-schedule-templates/_views/_shared/template-name-with-tags";
import { FeeScheduleTemplateJSON } from "work/fee-schedule-templates/_schemas/fee-schedule-template-schema";
import FeeScheduleTemplate from "work/fee-schedule-templates/fee-schedule-template";
import { TemplateUsageKind } from "work/template-usages/_schemas/template-usage-schema";
import TemplateDetailsDrawer from "work/template-usages/_views/template-details-drawer";
import { FeeScheduleTemplateOrderField } from "work/fee-schedule-templates/_api/queries/contracts/ordering/fee-schedule-template-order-field";
import {
  FeeScheduleLoadIssue,
  FeeScheduleListItemJSON,
  useFeeScheduleListCounts,
  useGetFeeSchedulesByQueryQuery,
} from "work/fee-schedules/_api/fee-schedules-api";
import FeeSchedulesQuery from "work/fee-schedules/_api/queries/contracts/fee-schedules-query";
import FeeSchedulesFilterParameters from "work/fee-schedules/_api/queries/contracts/filtering/fee-schedules-filter-parameters";
import FeeSchedulesOrderParameters, {
  FeeSchedulesOrderField,
} from "work/fee-schedules/_api/queries/contracts/ordering/fee-schedules-order-parameters";
import {
  getFeeSchedulesByQuery,
  getFeeSchedulesLastSuccessfulResponseByInterface,
  getFeeSchedulesQueryByInterface,
  isFeeScheduleLoadIssue,
  updateFeeSchedulesInterfaceQuery,
} from "work/fee-schedules/_store/fee-schedules-redux-slice";
import FeeSchedule from "work/fee-schedules/fee-schedule";
import BarebonesWorkAgreementResponse from "work/work-agreements/_api/responses/barebones-work-agreement-response";
import { useGetWorkAgreementsByIdsQuery } from "work/work-agreements/_api/work-agreement-api";
import { getWorkAgreementsByIds } from "work/work-agreements/_store/work-agreement-redux-slice";
import { startDraftProposalFromFeeScheduleTemplate } from "work/proposals/_views/drafts/start-draft-proposal-from-template";
import {
  FeeScheduleTableCellText,
  feeScheduleTableCellStyles,
} from "_shared/fee-schedules/categories/_views/fee-schedule-table-cell";
import { NoValueDash } from "_shared/_views/tables/no-value-dash";
// #endregion

// #region Styled Components
const DetailsSummary = styled("dl")(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(1, 2),
  gridTemplateColumns: "auto 1fr",
  margin: 0,
  "& dt": { color: theme.palette.text.secondary },
  "& dd": { margin: 0, minWidth: 0 },
}));
const TemplateLinks = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.5),
  minWidth: 0,
}));
// #endregion

type ActiveFeeScheduleRow = (FeeSchedule | FeeScheduleLoadIssue) & {
  id: Guid;
  linkedFeeScheduleTemplates?: LinkedFeeScheduleTemplate[];
  workAgreement?: BarebonesWorkAgreementResponse;
};
type FeeSchedulePageRow = FeeScheduleTemplate | FeeScheduleTemplateLoadIssue | ActiveFeeScheduleRow;
type LinkedFeeScheduleTemplate = {
  id: Guid;
  template?: FeeScheduleTemplateJSON;
};

const columnHelper = createMRTColumnHelper<FeeSchedulePageRow>();
type DeserializedFeeScheduleTemplateRow = (FeeScheduleTemplate | FeeScheduleTemplateLoadIssue) & { id: Guid };

/** Where a page's columns go when the pane is short of room (#6711): each onto its tab's name. */
const feeScheduleColumnFolds = {
  tags: { into: "name", priority: 1 },
  template: { into: "name", priority: 1 },
} as const;

/**
 * The field each tab's list orders by, for each column it sorts. In use orders its hire date by
 * `CreatedDate`, which the server reads as the hire date of the work.
 */
const orderFieldByColumnId: Readonly<Record<PageTab, Readonly<Record<string, string>>>> = {
  [PageTab.Templates]: {
    name: FeeScheduleTemplateOrderField.Name,
    tags: FeeScheduleTemplateOrderField.Tags,
    createdDate: FeeScheduleTemplateOrderField.CreatedDate,
  },
  [PageTab.InUse]: {
    name: FeeSchedulesOrderField.Name,
    hireDate: FeeSchedulesOrderField.CreatedDate,
  },
};

/** What each tab, with or without its Archived chip, lists: for its search box and its empty state. */
function listSubjectOf(tab: PageTab, isArchive: boolean): string {
  if (tab === PageTab.InUse) return isArchive ? "fee schedules of archived work" : "fee schedules in use";
  return isArchive ? "archived templates" : "templates";
}

function deserializeFeeScheduleTemplates(
  feeScheduleTemplateJsonList: Array<FeeScheduleTemplateJSON | FeeScheduleTemplateLoadIssue>,
): DeserializedFeeScheduleTemplateRow[] {
  const deserializedData: DeserializedFeeScheduleTemplateRow[] = [];

  for (const feeScheduleTemplateJson of feeScheduleTemplateJsonList) {
    try {
      if (isFeeScheduleTemplateLoadIssue(feeScheduleTemplateJson)) {
        deserializedData.push(feeScheduleTemplateJson);
        continue;
      }

      const feeScheduleTemplate = FeeScheduleTemplate.fromJSON(feeScheduleTemplateJson);
      if (!feeScheduleTemplate?.id) {
        console.error("FeeScheduleTemplate does not have an id", feeScheduleTemplateJson);
        deserializedData.push({ id: Guid.EMPTY, issue: "Error loading FeeScheduleTemplate." });
        continue;
      }

      deserializedData.push(feeScheduleTemplate as FeeScheduleTemplate & { id: Guid });
    } catch (error) {
      const id = "id" in feeScheduleTemplateJson ? (Guid.fromJSON(feeScheduleTemplateJson.id) ?? Guid.EMPTY) : Guid.EMPTY;
      deserializedData.push({ id, issue: "Error loading FeeScheduleTemplate." });
      console.error("Error deserializing FeeScheduleTemplate JSON:", error);
    }
  }

  return deserializedData;
}

function deserializeActiveFeeSchedules(
  feeScheduleJsonList: Array<FeeScheduleListItemJSON | FeeScheduleLoadIssue>,
): ActiveFeeScheduleRow[] {
  const deserializedData: ActiveFeeScheduleRow[] = [];

  for (const feeScheduleJson of feeScheduleJsonList) {
    try {
      if (isFeeScheduleLoadIssue(feeScheduleJson)) {
        deserializedData.push({ ...feeScheduleJson, id: feeScheduleJson.id });
        continue;
      }

      const feeSchedule = FeeSchedule.fromJSON(feeScheduleJson) as FeeSchedule & { id: Guid };
      feeSchedule.id = feeSchedule.workAgreementId;
      deserializedData.push(feeSchedule);
    } catch (error) {
      const id = "workAgreementId" in feeScheduleJson
        ? (Guid.fromJSON(feeScheduleJson.workAgreementId) ?? Guid.EMPTY)
        : Guid.EMPTY;
      deserializedData.push({ id, issue: "Error loading Fee Schedule." });
      console.error("Error deserializing FeeSchedule JSON:", error);
    }
  }

  return deserializedData;
}

/**
 * The In use tab's list: the fee schedules of current work, or with the Archived chip those of
 * archived work. Archived work is not active, so the archive leaves active unsaid.
 */
function applyInUseFilter(filterParameters: FeeSchedulesFilterParameters, isArchive: boolean): FeeSchedulesFilterParameters {
  return new FeeSchedulesFilterParameters({
    ...filterParameters,
    isActive: isArchive ? undefined : new AHBoolean(true),
    isArchived: new AHBoolean(isArchive),
  });
}

/**
 * The Templates tab's list: the templates, or with the Archived chip the archived ones (#6716). The
 * old Archived tab left `isActive` unset and listed archived templates beside the fee schedules of
 * archived work; the chip lists templates only.
 */
function applyTemplatesFilter(
  filterParameters: FeeScheduleTemplateFilterParameters,
  isArchive: boolean,
): FeeScheduleTemplateFilterParameters {
  return new FeeScheduleTemplateFilterParameters({
    ...filterParameters,
    isActive: new AHBoolean(false),
    isArchived: new AHBoolean(isArchive),
  });
}

/**
 * What a tab's counts request is narrowed by: its list's criteria, less the archived state the counts
 * split on. The tab on screen counts under its own search and filters; the other tab under none,
 * since its list has none until the reader goes there.
 */
export function feeScheduleCountsCriteria(
  tab: PageTab,
  searchParams: URLSearchParams,
  session: Session,
): string {
  const criteria = tab === PageTab.InUse
    ? FeeSchedulesFilterParameters.fromSearchParams({ searchParams, session }).searchParams
    : FeeScheduleTemplateFilterParameters.fromSearchParams({ searchParams, session }).searchParams;
  criteria.delete("isArchived");
  criteria.delete("isActive");
  return criteria.toString();
}

function getFeeScheduleTemplateIdsForSession(feeSchedule: FeeSchedule, viewingAsVendor: boolean | undefined): Guid[] {
  return viewingAsVendor ? feeSchedule.vendorFeeScheduleTemplateIds : feeSchedule.clientFeeScheduleTemplateIds;
}

function getUniqueFeeScheduleTemplateIds(
  rows: ActiveFeeScheduleRow[] | undefined,
  viewingAsVendor: boolean | undefined,
): Guid[] {
  const idsByValue = new Map<string, Guid>();

  for (const row of rows ?? []) {
    if (isFeeScheduleLoadIssue(row)) continue;

    for (const id of getFeeScheduleTemplateIdsForSession(row, viewingAsVendor)) {
      idsByValue.set(id.value, id);
    }
  }

  return [...idsByValue.values()];
}

/** The table's own state, as the URL holds it (#6712): the search, the filters, the sort and the page. */
type FeeScheduleTableState = {
  globalFilter: string;
  columnFilters: ColumnFiltersState;
  sorting: SortingState;
  pagination: PaginationState;
};

function readTableState(searchParams: URLSearchParams, tab: PageTab): FeeScheduleTableState {
  const columnFilters: ColumnFiltersState = [];
  if (tab === PageTab.Templates) {
    const name = searchParams.get("name");
    const tags = searchParams.getAll("tags");
    if (name) columnFilters.push({ id: "name", value: name });
    if (tags.length) columnFilters.push({ id: "tags", value: tags });
  }

  const sorting: SortingState = [];
  const orderBy = searchParams.get("orderBy")?.toLowerCase();
  const columnId = Object.entries(orderFieldByColumnId[tab]).find(([, field]) => field.toLowerCase() === orderBy)?.[0];
  if (columnId) sorting.push({ id: columnId, desc: searchParams.get("orderDir") !== OrderDirection.Ascending });

  const { pageIndex, pageSize } = PaginationParameters.fromListPageSearchParams(searchParams);
  return { globalFilter: searchParams.get("query") ?? "", columnFilters, sorting, pagination: { pageIndex, pageSize } };
}

/** `current` with the table's state written over it. The chips are the page's, and stay as they are. */
function writeTableState(state: FeeScheduleTableState, current: URLSearchParams, tab: PageTab): URLSearchParams {
  const next = new URLSearchParams();
  for (const stage of current.getAll(STAGE_PARAMETER)) next.append(STAGE_PARAMETER, stage);
  if (state.globalFilter) next.set("query", state.globalFilter);

  if (tab === PageTab.Templates) {
    const nameFilter = state.columnFilters.find((filter) => filter.id === "name")?.value as string | undefined;
    if (nameFilter) next.set("name", nameFilter);
    const tagsFilter = state.columnFilters.find((filter) => filter.id === "tags")?.value as string[] | undefined;
    tagsFilter?.forEach((tag) => next.append("tags", tag));
  }

  const sort = state.sorting.find((each) => orderFieldByColumnId[tab][each.id]);
  if (sort) {
    next.set("orderBy", orderFieldByColumnId[tab][sort.id]);
    next.set("orderDir", sort.desc ? OrderDirection.Descending : OrderDirection.Ascending);
  }

  next.set("pageIndex", String(state.pagination.pageIndex ?? 0));
  next.set("pageSize", String(state.pagination.pageSize ?? LIST_PAGE_SIZE));
  return next;
}

function applyUpdater<T>(updater: MRT_Updater<T>, previous: T): T {
  return typeof updater === "function" ? (updater as (previous: T) => T)(previous) : updater;
}

export default function FeeSchedulesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  // #region Hooks
  const navigate = useNavigate();
  const location = useLocation();
  const session = useSession();
  const confirm = useConfirmDialog();
  const dispatch = useAttorneyHubDispatch();
  // The view the reader left the page in, kept on their account (#6714): a plain visit to
  // `/fee-schedules` opens their starred view or that one, else Templates.
  const { remember } = useCurrentTableView(FEE_SCHEDULES_CURRENT_VIEW);

  // #region Route
  const { pageTab: pageTabParam } = useParams();
  const effectivePageTab = readFeeScheduleTab(pageTabParam);
  const isActiveTab = effectivePageTab === PageTab.InUse;

  // #region Chips
  // Each tab's Archived chip (#6716). Memoised on the URL because the list's request is built from it
  // in an effect, which must not run on every render.
  const searchKey = searchParams.toString();
  const chips = useMemo(() => readFeeScheduleChips(new URLSearchParams(searchKey)), [searchKey]);
  const isArchive = listsArchive(chips);
  const tableState = useMemo(
    () => readTableState(new URLSearchParams(searchKey), effectivePageTab),
    [effectivePageTab, searchKey],
  );
  const { globalFilter, columnFilters, sorting, pagination } = tableState;

  // The template whose details panel is open, and the list it was opened from.
  const listKey = `${effectivePageTab}:${isArchive}`;
  const [openDetails, setOpenDetails] = React.useState<{ template: FeeScheduleTemplate; list: string } | undefined>();
  // The page stays mounted across tabs and chips, so the panel is tied to the list it was opened
  // from: on another list it would describe a row that is not listed, and link to the wrong route.
  const detailsTemplate = openDetails?.list === listKey ? openDetails.template : undefined;

  // #region Permission Queries
  const sessionPermissions = useSelector(getSessionPermissions);
  const permissionsLoadState = useSessionPermissionsLoadState();

  // #endregion

  // #region Fee Schedule Queries
  const interfaceName = `feeSchedule:${listKey}`;
  const feeScheduleTemplatesQueryParams = useSelector(getFeeScheduleTemplateQueryByInterface(interfaceName));
  const feeSchedulesQueryParams = useSelector(getFeeSchedulesQueryByInterface(interfaceName));

  const paginatedFeeScheduleTemplatesJson = useSelector(getFeeScheduleTemplatesByQuery(feeScheduleTemplatesQueryParams));
  const paginatedFeeScheduleTemplates = useMemo(() => {
    if (!paginatedFeeScheduleTemplatesJson) return;
    return deserializeFeeScheduleTemplates(paginatedFeeScheduleTemplatesJson.data);
  }, [paginatedFeeScheduleTemplatesJson]);

  const feeScheduleTemplatesAPIQuery = useGetFeeScheduleTemplatesQuery(
    !isActiveTab && feeScheduleTemplatesQueryParams ? { query: feeScheduleTemplatesQueryParams, interfaceName } : skipToken,
  );
  const paginatedFeeSchedulesJson = useSelector(getFeeSchedulesByQuery(feeSchedulesQueryParams));
  const paginatedFeeSchedules = useMemo(() => {
    if (!paginatedFeeSchedulesJson) return;
    return deserializeActiveFeeSchedules(paginatedFeeSchedulesJson.data);
  }, [paginatedFeeSchedulesJson]);

  const feeSchedulesAPIQuery = useGetFeeSchedulesByQueryQuery(
    isActiveTab && feeSchedulesQueryParams ? { query: feeSchedulesQueryParams, interfaceName } : skipToken,
  );
  const [archiveFeeScheduleTemplate] = useArchiveFeeScheduleTemplateMutation();
  const [unarchiveFeeScheduleTemplate] = useUnarchiveFeeScheduleTemplateMutation();
  const [deleteFeeScheduleTemplate] = useDeleteFeeScheduleTemplateMutation();

  // Each tab's badge and its Archived chip's count, one counts request per list (#6709). The tab on
  // screen counts under its own criteria; a badge does not move with the chip.
  const templateCountsCriteria = useMemo(
    () => feeScheduleCountsCriteria(PageTab.Templates, new URLSearchParams(isActiveTab ? "" : searchKey), session),
    [isActiveTab, searchKey, session],
  );
  const inUseCountsCriteria = useMemo(
    () => feeScheduleCountsCriteria(PageTab.InUse, new URLSearchParams(isActiveTab ? searchKey : ""), session),
    [isActiveTab, searchKey, session],
  );
  const templateCounts = useFeeScheduleTemplateListCounts({ criteria: templateCountsCriteria });
  const inUseCounts = useFeeScheduleListCounts({ criteria: inUseCountsCriteria });
  const archivedCount = isActiveTab ? inUseCounts.counts?.archived : templateCounts.counts?.archived;
  const chipCounts = useMemo<ListChipCounts>(
    () => ({ [STAGE_PARAMETER]: { archived: archivedCount } }),
    [archivedCount],
  );
  const pageTabs = useMemo<readonly ListPageTab<PageTab>[]>(
    () => [
      {
        value: PageTab.Templates,
        label: "Templates",
        counts: "template(s)",
        count: templateCounts.counts?.templates,
        isLoading: templateCounts.isLoading,
        badgeColor: "info",
      },
      {
        value: PageTab.InUse,
        label: "In use",
        counts: "fee schedule(s) in use",
        count: inUseCounts.counts?.active,
        isLoading: inUseCounts.isLoading,
        badgeColor: "info",
      },
    ],
    [inUseCounts.counts?.active, inUseCounts.isLoading, templateCounts.counts?.templates, templateCounts.isLoading],
  );

  const lastSuccessfulTemplateQueryResponseJson = useSelector(getLastSuccessfulFeeScheduleTemplateQueryByInterface(interfaceName));

  const lastSuccessfulTemplateQueryResponse = useMemo(() => {
    if (!lastSuccessfulTemplateQueryResponseJson) return;
    return deserializeFeeScheduleTemplates(lastSuccessfulTemplateQueryResponseJson.data);
  }, [lastSuccessfulTemplateQueryResponseJson]);

  const lastSuccessfulFeeSchedulesQueryResponseJson = useSelector(getFeeSchedulesLastSuccessfulResponseByInterface(interfaceName));

  const lastSuccessfulFeeSchedulesQueryResponse = useMemo(() => {
    if (!lastSuccessfulFeeSchedulesQueryResponseJson) return;
    return deserializeActiveFeeSchedules(lastSuccessfulFeeSchedulesQueryResponseJson.data);
  }, [lastSuccessfulFeeSchedulesQueryResponseJson]);

  const tableTemplates = paginatedFeeScheduleTemplates ?? lastSuccessfulTemplateQueryResponse;
  const tableFeeSchedules = paginatedFeeSchedules ?? lastSuccessfulFeeSchedulesQueryResponse;
  const activeWorkAgreementIds = useMemo(
    () =>
      (tableFeeSchedules ?? [])
        .filter((row): row is FeeSchedule & { id: Guid } => !isFeeScheduleLoadIssue(row))
        .map((feeSchedule) => feeSchedule.workAgreementId),
    [tableFeeSchedules],
  );

  const workAgreementsByIdsAPIQuery = useGetWorkAgreementsByIdsQuery(
    isActiveTab && activeWorkAgreementIds.length
      ? { workAgreementIds: activeWorkAgreementIds.map((id) => id.value) }
      : skipToken,
  );
  const barebonesWorkAgreements = useSelector(getWorkAgreementsByIds(activeWorkAgreementIds));
  const barebonesWorkAgreementsById = useMemo(() => {
    return new Map(barebonesWorkAgreements.map((workAgreement) => [workAgreement.id, workAgreement]));
  }, [barebonesWorkAgreements]);
  const viewingAsVendor = session.context?.viewingAsVendor;
  const activeFeeScheduleTemplateIds = useMemo(
    () => getUniqueFeeScheduleTemplateIds(tableFeeSchedules, viewingAsVendor),
    [tableFeeSchedules, viewingAsVendor],
  );
  const activeFeeScheduleTemplatesByIdsAPIQuery = useGetFeeScheduleTemplatesByIdsQuery(
    isActiveTab && activeFeeScheduleTemplateIds.length
      ? { feeScheduleTemplateIds: activeFeeScheduleTemplateIds.map((id) => id.value) }
      : skipToken,
  );
  const activeFeeScheduleTemplates = useSelector(getFeeScheduleTemplatesByIds(activeFeeScheduleTemplateIds));
  const activeFeeScheduleTemplatesById = useMemo(() => {
    return new Map(
      activeFeeScheduleTemplates
        .filter((template): template is FeeScheduleTemplateJSON & { id: string } => Boolean(template?.id))
        .map((template) => [template.id, template]),
    );
  }, [activeFeeScheduleTemplates]);
  const tableActiveFeeSchedules = useMemo<ActiveFeeScheduleRow[] | undefined>(() => {
    if (!tableFeeSchedules) return;
    return tableFeeSchedules.map((feeSchedule) => {
      if (isFeeScheduleLoadIssue(feeSchedule)) return feeSchedule;
      feeSchedule.workAgreement = barebonesWorkAgreementsById.get(feeSchedule.workAgreementId.value);
      feeSchedule.linkedFeeScheduleTemplates = getFeeScheduleTemplateIdsForSession(feeSchedule, viewingAsVendor)
        .map((templateId) => ({
          id: templateId,
          template: activeFeeScheduleTemplatesById.get(templateId.value),
        }));
      return feeSchedule;
    });
  }, [activeFeeScheduleTemplatesById, barebonesWorkAgreementsById, tableFeeSchedules, viewingAsVendor]);

  useEffect(() => {
    if (isActiveTab) {
      const filterParameters = applyInUseFilter(
        FeeSchedulesFilterParameters.fromSearchParams({ searchParams, session }),
        isArchive,
      );
      const orderParameters = FeeSchedulesOrderParameters.fromSearchParams(searchParams);

      const newQuery = new FeeSchedulesQuery({
        filterParameters,
        orderParameters,
        paginationParameters: PaginationParameters.fromListPageSearchParams(searchParams),
      });
      if (feeSchedulesQueryParams && newQuery.isEqualTo(feeSchedulesQueryParams)) return;
      dispatch(
        updateFeeSchedulesInterfaceQuery({
          interfaceName,
          query: newQuery,
        }),
      );
      return;
    }

    const filterParameters = applyTemplatesFilter(
      FeeScheduleTemplateFilterParameters.fromSearchParams({ searchParams, session }),
      isArchive,
    );
    const orderParameters = FeeScheduleTemplateOrderParameters.fromSearchParams(searchParams);

    const newQuery = new FeeScheduleTemplateQuery({
      filterParameters,
      orderParameters,
      paginationParameters: PaginationParameters.fromListPageSearchParams(searchParams),
    });
    if (feeScheduleTemplatesQueryParams && newQuery.isEqualTo(feeScheduleTemplatesQueryParams)) return;
    dispatch(
      updateFeeScheduleTemplateInterfaceQuery({
        interfaceName: interfaceName,
        query: newQuery,
      }),
    );
  }, [
    isArchive,
    session.user?.legalEntityId.value,
    session,
    searchParams,
    feeScheduleTemplatesQueryParams,
    feeSchedulesQueryParams,
    dispatch,
    interfaceName,
    isActiveTab,
  ]);

  useEffect(() => {
    if (feeScheduleTemplatesAPIQuery.isError) {
      enqueueSnackbar("Error loading fee schedule templates table", { variant: "error" });
    }
  }, [feeScheduleTemplatesAPIQuery.isError]);

  useEffect(() => {
    if (feeSchedulesAPIQuery.isError) {
      enqueueSnackbar("Error loading fee schedules in use", { variant: "error" });
    }
  }, [feeSchedulesAPIQuery.isError]);

  // #region URL
  /** Shows `next` and keeps it as the reader's view: every change the reader makes goes through here. */
  const showReaderChange = useCallback(
    (next: URLSearchParams) => {
      if (next.toString() === searchParams.toString()) return;
      remember(`${location.pathname}?${next.toString()}`);
      setSearchParams(next);
    },
    [location.pathname, remember, searchParams, setSearchParams],
  );

  const updateTableState = useCallback(
    (change: Partial<FeeScheduleTableState>) =>
      showReaderChange(writeTableState({ ...tableState, ...change }, searchParams, effectivePageTab)),
    [effectivePageTab, searchParams, showReaderChange, tableState],
  );

  const setGlobalFilter = useCallback(
    (updater: MRT_Updater<string>) => updateTableState({ globalFilter: applyUpdater(updater, globalFilter) ?? "" }),
    [globalFilter, updateTableState],
  );
  const setColumnFilters = useCallback(
    (updater: MRT_Updater<ColumnFiltersState>) => updateTableState({ columnFilters: applyUpdater(updater, columnFilters) }),
    [columnFilters, updateTableState],
  );
  const setSorting = useCallback(
    (updater: MRT_Updater<SortingState>) => updateTableState({ sorting: applyUpdater(updater, sorting) }),
    [sorting, updateTableState],
  );
  const setPagination = useCallback(
    (updater: MRT_Updater<PaginationState>) => updateTableState({ pagination: applyUpdater(updater, pagination) }),
    [pagination, updateTableState],
  );

  const handleTabChange = useCallback(
    (tab: PageTab) => {
      // A tab is a different kind of thing, so nothing the other tab's chips or filters said goes with it.
      const path = feeScheduleTabPath(tab);
      remember(path);
      navigate(path);
    },
    [navigate, remember],
  );

  const handleChipsChange = useCallback(
    (selection: ListChipSelection) => showReaderChange(writeFeeScheduleChips(selection, searchParams)),
    [searchParams, showReaderChange],
  );
  // #endregion

  // #region Functions
  /**
   * Archive or Unarchive the specified fee schedule template
   * @param id fee schedule id to archive / unarchive
   * @param archive if true, archive, else unarchive
   */
  const handleArchiveFeeScheduleTemplate = useCallback(async (id: Guid | undefined, archive: boolean): Promise<void> => {
    if (!id) {
      throw new Error("No fee schedule template id provided");
    }

    const action = archive ? "Archive" : "Unarchive";

    const response = await confirm({
      title: action,
      message: `${action} fee schedule template?`,
      okButtonText: action,
      cancelButtonText: "Cancel",
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      if (archive) await archiveFeeScheduleTemplate({ templateId: id.value }).unwrap();
      else await unarchiveFeeScheduleTemplate({ templateId: id.value }).unwrap();
      enqueueSnackbar(`${action} fee schedule`, { variant: "success" });
      dispatch(resetFeeScheduleTemplatesByQueryState());
    } catch (error) {
      console.error(error);
      enqueueSnackbar(`Failed to ${action} fee schedule. Please try again`, {
        variant: "error",
      });
    }
  }, [confirm, archiveFeeScheduleTemplate, unarchiveFeeScheduleTemplate, dispatch]);

  /**
   * Deletes the specified fee schedule template
   * @param id The fee schedule template id
   */
  const handleDeleteFeeScheduleTemplate = useCallback(async (id: Guid | undefined): Promise<void> => {
    if (!id) {
      throw new Error("No fee schedule template id provided");
    }

    const response = await confirm({
      title: "Delete?",
      message: `Delete fee schedule template?`,
      okButtonText: "Delete",
      cancelButtonText: "Cancel",
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      await deleteFeeScheduleTemplate({ templateId: id.value }).unwrap();
      enqueueSnackbar("Deleted fee schedule", { variant: "success" });
      dispatch(resetFeeScheduleTemplatesByQueryState());
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to delete fee schedule. Please try again", {
        variant: "error",
      });
    }
  }, [confirm, dispatch, deleteFeeScheduleTemplate]);

  const handleCreateProposalFromFeeScheduleTemplate = useCallback((template: FeeScheduleTemplate): void => {
    startDraftProposalFromFeeScheduleTemplate({
      dispatch,
      navigate,
      session,
      template,
    });
  }, [dispatch, navigate, session]);
  // #endregion

  const columns = useMemo(
    () => {
      if (isActiveTab) {
        // The work agreement carries its description, and the template it came from folds under it
        // when the pane is short of room (#6716). The Description column stays in Show/hide.
        return [
          columnHelper.accessor((row) => "workAgreementId" in row ? row.workAgreement?.name ?? "" : "", {
            header: "Work agreement",
            id: "name",
            meta: { kind: "name" },
            enableColumnFilter: false,
            Cell: ({ row, table }) => <ActiveWorkAgreementCell row={row} table={table} isArchive={isArchive} />,
          }),
          columnHelper.display({
            header: "From template",
            id: "template",
            meta: { kind: "text", fold: { ...feeScheduleColumnFolds.template, render: foldedTemplates } },
            enableSorting: false,
            enableColumnFilter: false,
            Cell: ActiveTemplateCell,
          }),
          columnHelper.display({
            header: "Categories",
            id: "categories",
            meta: { kind: "number" },
            enableSorting: false,
            enableColumnFilter: false,
            Cell: ActiveCategoriesCell,
          }),
          // An accessor, not a display column: TanStack sorts only a column with one. The server sorts it by
          // CreatedDate (`orderFieldByColumnId`); the value only lets the table offer the sort.
          columnHelper.accessor(
            (row) => "workAgreementId" in row && !isFeeScheduleLoadIssue(row) ? row.hireDate?.toString() ?? "" : "",
            {
              header: "Hired",
              id: "hireDate",
              meta: { kind: "date" },
              enableSorting: true,
              enableColumnFilter: false,
              // The server's search never reads a date (#6590).
              enableGlobalFilter: false,
              Cell: ActiveHireDateCell,
            },
          ),
          columnHelper.display({
            header: "Description",
            id: "description",
            meta: { kind: "text" },
            enableSorting: false,
            enableColumnFilter: false,
            Cell: ActiveDescriptionCell,
          }),
        ];
      }

      return [
        // Asked of the instance, not of the row's shape: while it loads with no data,
        // material-react-table fills the body with placeholder rows of its own — plain objects
        // holding `null` under every column id — and `tags.map` on one of those takes the page down.
        columnHelper.accessor((row) => row instanceof FeeScheduleTemplate ? row.name : "", {
          header: "Name",
          id: "name",
          meta: { kind: "name" },
          enableColumnFilter: true,
          Cell: ({ row, table }) => <NameCell row={row} table={table} isArchive={isArchive} />
        }),
        columnHelper.accessor(tagNames, {
          header: "Tags",
          id: "tags",
          meta: { kind: "tags", fold: { ...feeScheduleColumnFolds.tags, render: foldedTags } },
          // The multi-select filter's options are this column's unique values, which material-react-table
          // sorts with `localeCompare`. Without this, a row's whole tag array is one value (#5779).
          getUniqueValues: tagNames,
          enableSorting: true,
          enableColumnFilter: true,
          filterVariant: "multi-select",
          Cell: TagsCell
        }),
        columnHelper.accessor((row) => row instanceof FeeScheduleTemplate ? row.created?.toString() ?? "" : "", {
          header: "Created",
          id: "createdDate",
          meta: { kind: "date" },
          enableColumnFilter: false,
          // The server's search reads a template's name, tags and categories, never its date (#6590).
          enableGlobalFilter: false,
          Cell: CreatedDateCell
        }),
      ];
    },
    [isActiveTab, isArchive],
  );

  const openFeeSchedule = useCallback(
    (feeSchedule: FeeSchedulePageRow) => {
      if (isActiveTab) {
        if (isFeeScheduleLoadIssue(feeSchedule)) {
          console.warn("Cannot open fee schedule due to load issue:", feeSchedule.issue);
          return;
        }
        const destination = feeScheduleInUseDestination(feeSchedule, isArchive);
        if (destination) navigate(destination);
        return;
      }

      if (isFeeScheduleTemplateLoadIssue(feeSchedule)) {
        console.warn("Cannot open fee schedule template due to load issue:", feeSchedule.issue);
        return;
      }
      const destination = feeScheduleTemplateDestination(feeSchedule, isArchive);
      if (!destination) {
        console.error("Cannot open fee schedule template without id");
        return;
      }

      navigate(destination);
    },
    [isActiveTab, isArchive, navigate]
  );

  const muiTableBodyRowProps = useCallback(
    ({ row }: { row: MRT_Row<FeeSchedulePageRow> }) => ({
      onClick: () => openFeeSchedule(row.original),
      sx: {
        backgroundColor: isFeeScheduleTemplateLoadIssue(row.original)
          ? "rgba(255, 100, 100, 0.1)" : "inherit",
        cursor: isFeeScheduleTemplateLoadIssue(row.original) ? "default" : "pointer",
      },
    }),
    [openFeeSchedule]
  );

  const renderToolbarInternalActions = useCallback(
    (props: { table: MRT_TableInstance<FeeSchedulePageRow> }) => (
      <FeeScheduleInternalActionsToolbar table={props.table} />
    ), []
  );

  const renderTopToolbarCustomActions = useCallback(
    ({ table }: { table: MRT_TableInstance<FeeSchedulePageRow> }) => (
      <FeeScheduleToolbar
        table={table}
        activeTab={effectivePageTab}
        tabs={pageTabs}
        onTabChange={handleTabChange}
        chips={chips}
        chipCounts={chipCounts}
        onChipsChange={handleChipsChange}
      />
    ), [chipCounts, chips, effectivePageTab, handleChipsChange, handleTabChange, pageTabs]
  );

  // The ⋯ that ends every row, with Open first (#6711): to what the row and its name open.
  const renderRowActions = useCallback(
    (props: { row: MRT_Row<FeeSchedulePageRow> }) => {
      const { row } = props;
      if (isActiveTab) {
        if (isFeeScheduleLoadIssue(row.original)) return null;
        const destination = feeScheduleInUseDestination(row.original, isArchive);
        if (!destination) return null;
        const name = "workAgreement" in row.original ? row.original.workAgreement?.name : undefined;
        return <RowActions actions={[]} open={{ to: destination }} ariaLabel={name ? `Actions for ${name}` : undefined} />;
      }
      if (isFeeScheduleTemplateLoadIssue(row.original)) return null;
      const destination = feeScheduleTemplateDestination(row.original, isArchive);
      return (
        <FeeScheduleRowActions
          row={row as MRT_Row<FeeScheduleTemplate | FeeScheduleTemplateLoadIssue>}
          isArchived={isArchive}
          open={destination ? { to: destination } : undefined}
          onArchivedChange={handleArchiveFeeScheduleTemplate}
          onCreateProposal={handleCreateProposalFromFeeScheduleTemplate}
          onDelete={handleDeleteFeeScheduleTemplate}
          onShowDetails={(template) => setOpenDetails({ template, list: listKey })}
        />
      );
    },
    [
      handleArchiveFeeScheduleTemplate,
      handleCreateProposalFromFeeScheduleTemplate,
      handleDeleteFeeScheduleTemplate,
      isActiveTab,
      isArchive,
      listKey,
    ]
  );

  const getRowId = useCallback((row: FeeSchedulePageRow, index: number) => {
    if (isFeeScheduleTemplateLoadIssue(row)) return `template-load-issue-${row.id.value}-${index}`;
    if (isFeeScheduleLoadIssue(row)) return `fee-schedule-load-issue-${row.id.value}-${index}`;
    if ("workAgreementId" in row) return row.workAgreementId.value;
    // MRT's loading placeholders carry no id, and one key for all of them is a duplicate React key.
    return row.id?.value ?? `placeholder-${index}`;
  }, []);

  const muiToolbarAlertBannerProps = useMemo(() => {
    if (isActiveTab && feeSchedulesAPIQuery.isError) {
      return { color: "error" as const, children: "Error loading data" };
    }
    if (!isActiveTab && feeScheduleTemplatesAPIQuery.isError) {
      return { color: "error" as const, children: "Error loading data" };
    }
  }, [feeScheduleTemplatesAPIQuery.isError, feeSchedulesAPIQuery.isError, isActiveTab]);

  const tableRows = isActiveTab ? tableActiveFeeSchedules : tableTemplates;
  const tableHeaders = isActiveTab
    ? paginatedFeeSchedulesJson?.headers ?? lastSuccessfulFeeSchedulesQueryResponseJson?.headers
    : paginatedFeeScheduleTemplatesJson?.headers ?? lastSuccessfulTemplateQueryResponseJson?.headers;
  const tableIsFetching = isActiveTab
    ? feeSchedulesAPIQuery.isFetching || workAgreementsByIdsAPIQuery.isFetching || activeFeeScheduleTemplatesByIdsAPIQuery.isFetching
    : feeScheduleTemplatesAPIQuery.isFetching;
  const tableIsLoading = isActiveTab ? feeSchedulesAPIQuery.isLoading : feeScheduleTemplatesAPIQuery.isLoading;
  const tableIsError = isActiveTab ? feeSchedulesAPIQuery.isError : feeScheduleTemplatesAPIQuery.isError;
  const listSubject = listSubjectOf(effectivePageTab, isArchive);

  const tableOptions = useMemo<MRT_TableOptions<FeeSchedulePageRow>>(() => {
    return {
      columns,
      data: tableRows ?? [],
      enableRowSelection: false,
      enableTableHead: true,
      muiTableBodyRowProps,
      muiTableBodyCellProps: ({ cell }) => ({
        sx: (theme) => feeScheduleTableCellStyles(theme, {
          columnId: cell.column.id,
          truncate: cell.column.id !== "tags" && cell.column.id !== "template",
        }),
      }),
      renderToolbarInternalActions,
      renderTopToolbarCustomActions,
      getRowId,
      enableRowActions: true,
      enableColumnPinning: true,
      enableFacetedValues: true,
      initialState: {
        columnPinning: { right: ["mrt-row-actions"] },
        columnVisibility: { description: false },
        showGlobalFilter: true,
      },
      positionGlobalFilter: "none",
      muiSearchTextFieldProps: ({ table }) =>
        searchTextFieldProps({ searches: listSubject, table }),
      renderEmptyRowsFallback: emptyRowsFallback({
        isError: Boolean(tableIsError),
        isLoading: tableIsFetching,
        onRetry: () => void (isActiveTab ? feeSchedulesAPIQuery.refetch() : feeScheduleTemplatesAPIQuery.refetch()),
        // Each tab and chip holds something different, and the tab's own name is not it.
        subject: listSubject === "templates" ? "fee schedule templates" : listSubject,
      }),
      manualFiltering: true,
      enableColumnFilters: !isActiveTab,
      enableStickyHeader: true,
      enableStickyFooter: true,
      manualPagination: true,
      manualSorting: true,
      renderRowActions,
      muiTopToolbarProps: { sx: topToolbarFitsThePage },
      muiTableContainerProps: {
        sx: {
          flex: 1,
        },
      },
      muiTablePaperProps: {
        sx: {
          height: "100%",
          display: "flex",
          flexDirection: "column",
        },
      },
      muiToolbarAlertBannerProps,
      onColumnFiltersChange: setColumnFilters,
      onGlobalFilterChange: setGlobalFilter,
      onPaginationChange: setPagination,
      onSortingChange: setSorting,
      rowCount: tableHeaders?.totalElements ?? tableRows?.length ?? 0,
      state: {
        columnFilters,
        globalFilter,
        isLoading: tableIsFetching,
        pagination,
        showAlertBanner: Boolean(tableIsError),
        showProgressBars: tableIsFetching,
        sorting,
        showSkeletons: tableIsLoading,
      },
    };
  }, [
    columns,
    tableRows,
    tableHeaders,
    muiTableBodyRowProps,
    renderToolbarInternalActions,
    renderTopToolbarCustomActions,
    getRowId,
    renderRowActions,
    muiToolbarAlertBannerProps,
    columnFilters,
    globalFilter,
    listSubject,
    tableIsFetching,
    tableIsError,
    tableIsLoading,
    isActiveTab,
    feeSchedulesAPIQuery,
    feeScheduleTemplatesAPIQuery,
    pagination,
    sorting,
    setColumnFilters,
    setGlobalFilter,
    setPagination,
    setSorting,
  ]);

  // #region Render
  if (!sessionPermissions) return permissionsLoadState;
  if (!sessionPermissions.includesAccessToFeeSchedules) return <NotAuthorized />;

  return (
    <>
      <MRTNoMemo<FeeSchedulePageRow> options={tableOptions} persistColumnVisibilityAs="fee-schedules" cards />
      <TemplateDetailsDrawer
        templateId={detailsTemplate?.id?.value}
        kind={TemplateUsageKind.FeeSchedule}
        title={detailsTemplate?.name || "Fee schedule template"}
        templateHref={detailsTemplate ? feeScheduleTemplateDestination(detailsTemplate, isArchive) : undefined}
        onClose={() => setOpenDetails(undefined)}
        summary={detailsTemplate && (
          <DetailsSummary>
            <dt>Categories</dt>
            <dd>{detailsTemplate.categories.length}</dd>
            <dt>Tags</dt>
            <dd>{detailsTemplate.tags.length ? detailsTemplate.tags.map((tag) => tag.value).join(", ") : "None"}</dd>
            {detailsTemplate.created && (
              <>
                <dt>Created</dt>
                <dd><DateCell value={detailsTemplate.created} /></dd>
              </>
            )}
          </DetailsSummary>
        )}
      />
    </>
  );
  // #endregion
}

/**
 * Where a template goes, in one place, because the row, the link in its name cell and its Open item
 * have to agree. A template opens in its editor; an archived one opens read-only.
 */
export function templateDestination(templateId: string, isArchived: boolean): string {
  return isArchived ? `/fee-schedules/archived/view/${templateId}` : `/fee-schedules/${PageTab.Templates}/edit/${templateId}`;
}

function feeScheduleTemplateDestination(row: FeeSchedulePageRow, isArchive: boolean): string | undefined {
  if (isFeeScheduleTemplateLoadIssue(row)) return undefined;
  if ("workAgreementId" in row) return undefined;
  if (!row.id) return undefined;
  return templateDestination(row.id.value, isArchive);
}

/** Where a fee schedule in use goes: its work agreement's Fee Schedule tab. */
function feeScheduleInUseDestination(row: FeeSchedulePageRow, isArchive: boolean): string | undefined {
  if (!("workAgreementId" in row)) return undefined;
  return `/work/${isArchive ? "archived" : "active"}/view/${row.workAgreementId.value}/fee-schedule`;
}

function tagNames(row: FeeSchedulePageRow): string[] {
  return row instanceof FeeScheduleTemplate ? row.tags.map((tag) => tag.value) : [];
}

/** The tags as they read folded under the name: "Litigation, Patents". */
function foldedTags(row: FeeSchedulePageRow): string | undefined {
  const names = tagNames(row);
  return names.length ? names.join(", ") : undefined;
}

/** The templates a fee schedule came from, as they read folded under its work agreement: "From Standard rates". */
function foldedTemplates(row: FeeSchedulePageRow): string | undefined {
  if (!("linkedFeeScheduleTemplates" in row)) return undefined;
  const names = (row.linkedFeeScheduleTemplates ?? []).map(({ id, template }) => template?.name ?? id.value);
  return names.length ? `From ${names.join(", ")}` : undefined;
}

const NameCell = ({ row, table, isArchive }: {
  row: { original: FeeSchedulePageRow },
  table: MRT_TableInstance<FeeSchedulePageRow>,
  isArchive: boolean
}) => {
  // MRT's table instance is one mutable object, the same prop on every render, so a memoized
  // <SearchMatch table={table} /> would keep the search term it first marked (review of #6801).
  "use no memo";

  if (table.getState().isLoading) return;
  if (isFeeScheduleTemplateLoadIssue(row.original)) {
    return (
      <Tooltip
        slotProps={{
          tooltip: {
            sx: {
              maxWidth: "4000px",
              whiteSpace: "normal",
            },
          },
        }}
        title={`Our staff has been notified. Reference id: ${row.original.id.value}`}
      >
        <span>Error Loading Template</span>
      </Tooltip>
    );
  }
  if ("workAgreementId" in row.original) return;
  return (
    <FeeScheduleTableCellText emphasized>
      <RowLink to={feeScheduleTemplateDestination(row.original, isArchive)}>
        <SearchMatch table={table} text={row.original.name} />
      </RowLink>
    </FeeScheduleTableCellText>
  );
};

const TagsCell = ({ row, table }: {
  row: { original: FeeSchedulePageRow },
  table: MRT_TableInstance<FeeSchedulePageRow>
}) => {
  // MRT's table instance is one mutable object, the same prop on every render, so a memoized
  // <SearchMatch table={table} /> would keep the search term it first marked (review of #6801).
  "use no memo";

  if (table.getState().isLoading) return;
  if (isFeeScheduleTemplateLoadIssue(row.original)) return;
  if ("workAgreementId" in row.original) return;
  const template = row.original as FeeScheduleTemplate;
  if (!template.tags.length) return <NoValueDash />;
  // As many as the column has room for, then "+N" (#6609).
  return (
    // The search reads tags here, so the chips mark it (#6590).
    <SearchMatchScope table={table}>
      <TemplateTagLine tags={template.tags.map((tag) => tag.value)} />
    </SearchMatchScope>
  );
};

const CreatedDateCell = ({ row, table }: {
  row: { original: FeeSchedulePageRow },
  table: MRT_TableInstance<FeeSchedulePageRow>
}) => {
  if (table.getState().isLoading) return;
  if ("workAgreementId" in row.original) return;
  if (isFeeScheduleTemplateLoadIssue(row.original)) return;
  return <DateCell value={row.original.created} />;
};

/** The work agreement a fee schedule is in use on, as a link to it, over its description (#6716). */
const ActiveWorkAgreementCell = ({ row, table, isArchive }: {
  row: { original: FeeSchedulePageRow },
  table: MRT_TableInstance<FeeSchedulePageRow>,
  isArchive: boolean
}) => {
  // MRT's table instance is one mutable object, the same prop on every render, so a memoized
  // <SearchMatch table={table} /> would keep the search term it first marked (review of #6801).
  "use no memo";

  if (table.getState().isLoading) return;
  if (isFeeScheduleLoadIssue(row.original)) {
    return (
      <Tooltip title={`Our staff has been notified. Reference id: ${row.original.id.value}`}>
        <span>Error Loading Fee Schedule</span>
      </Tooltip>
    );
  }
  if (!("workAgreementId" in row.original)) return;
  const value = row.original.workAgreement?.name ?? row.original.workAgreementId.value;
  const description = row.original.workAgreement?.description;
  return (
    <CondensedCell
      title={
        <FeeScheduleTableCellText emphasized title={value}>
          <RowLink to={feeScheduleInUseDestination(row.original, isArchive)}>
            <SearchMatch table={table} text={value} />
          </RowLink>
        </FeeScheduleTableCellText>
      }
      // The server's search reads the agreement's description as well as its name (#6590).
      secondary={description ? <SearchMatch table={table} text={description} /> : undefined}
    />
  );
};

const ActiveDescriptionCell = ({ row, table }: {
  row: { original: FeeSchedulePageRow },
  table: MRT_TableInstance<FeeSchedulePageRow>
}) => {
  // MRT's table instance is one mutable object, the same prop on every render, so a memoized
  // <SearchMatch table={table} /> would keep the search term it first marked (review of #6801).
  "use no memo";

  if (table.getState().isLoading || isFeeScheduleLoadIssue(row.original) || !("workAgreementId" in row.original)) return;
  const description = row.original.workAgreement?.description;
  if (!description) return <NoValueDash />;
  // The server's search reads the agreement's description as well as its name (#6590).
  return (
    <FeeScheduleTableCellText title={description}>
      <SearchMatch table={table} text={description} />
    </FeeScheduleTableCellText>
  );
};

const ActiveHireDateCell = ({ row, table }: {
  row: { original: FeeSchedulePageRow },
  table: MRT_TableInstance<FeeSchedulePageRow>
}) => {
  if (table.getState().isLoading || isFeeScheduleLoadIssue(row.original) || !("workAgreementId" in row.original)) return;
  return <DateCell value={row.original.hireDate} />;
};

const ActiveCategoriesCell = ({ row, table }: {
  row: { original: FeeSchedulePageRow },
  table: MRT_TableInstance<FeeSchedulePageRow>
}) => {
  if (table.getState().isLoading || isFeeScheduleLoadIssue(row.original) || !("workAgreementId" in row.original)) return;
  return row.original.categories.length;
};

/** The templates the fee schedule came from, each a link to it (#6716). */
const ActiveTemplateCell = ({ row, table }: {
  row: { original: FeeSchedulePageRow },
  table: MRT_TableInstance<FeeSchedulePageRow>
}) => {
  if (table.getState().isLoading || isFeeScheduleLoadIssue(row.original) || !("workAgreementId" in row.original)) return;
  const linkedTemplates = row.original.linkedFeeScheduleTemplates ?? [];
  if (!linkedTemplates.length) return <NoValueDash />;

  return (
    <TemplateLinks>
      {linkedTemplates.map(({ id, template }) => (
        <Link
          key={id.value}
          component={RouterLink}
          to={templateDestination(id.value, Boolean(template?.archived))}
          onClick={(event) => event.stopPropagation()}
          noWrap
        >
          {template?.name ?? id.value}
        </Link>
      ))}
    </TemplateLinks>
  );
};
